import { useState, forwardRef } from 'react';

import { Box, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa';

import OfferItem from '../../../OfferItem/OfferItem';
import { FormattedPrice } from '../FormattedPrice/FormattedPrice';
import { CouponField } from '../Description/components/CouponField/CouponField';
import { useCheckout } from '../../../../hooks/use-checkout';
import { usePaymentState } from '../../hooks/use-payment-state';
import { useAppConfig } from '../../../../hooks/use-app-config';
import { useDeviceHelper } from '../../../../hooks/use-device-helper';

import './styles.scss';

export const SummaryLayout = forwardRef<HTMLDivElement, {}>((props, ref) => {
  const { checkoutData } = useCheckout();
  const { couponsConfiguration } = useAppConfig();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { isMobile } = useDeviceHelper();
  const priceBreakdown = usePaymentState((state) => state.priceBreakdown);
  const [expanded, setExpanded] = useState(false);

  const { items, pricing } = checkoutData || {};
  const showCouponField = couponsConfiguration?.enabled;
  const priceNet = priceBreakdown?.baseNet;
  const priceTotal = priceBreakdown?.priceTotal;
  const tax = priceBreakdown?.tax;
  const discount = priceBreakdown?.discount;
  const showTaxValue = priceBreakdown?.isTaxValid;

  return (
    <div className="supercell-template-summary" ref={ref}>
      <div className="summary-main">
        {!isMobile && (
          <div className="summary-title">
            <h2>{t('summary')}</h2>
          </div>
        )}
        {!isMobile && (
          <div className="summary-items">
            {items?.map((item) => (
              <OfferItem
                key={item.name}
                itemName={item.displayName || item.name}
                itemAmount={item.quantityDisplay || item.quantity}
                itemImage={item.assetUrl}
                itemPrice={item.priceDisplay}
              />
            ))}
          </div>
        )}

        {isMobile && (
          <Stack
            display={'flex'}
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
            alignSelf={'self-start'}
            height={16}
            fontSize={12}
            onClick={() => setExpanded(!expanded)}
          >
            <Box className="top-total-line">
              <FormattedPrice pricing={pricing} priceValue={priceTotal} data-testid="total-price" />
            </Box>
            <Stack direction={'row'} alignItems={'center'}>
              <span>{t('details')}</span>
              <Box pl={0.5} display={'flex'} alignItems={'center'}>
                {expanded ? <FaCaretUp /> : <FaCaretDown />}
              </Box>
            </Stack>
          </Stack>
        )}

        {showCouponField && (
          <Box pt={2}>
            <CouponField />
          </Box>
        )}

        {(expanded || !isMobile) && (
          <div className="summary-totals">
            <div className="summary-total-line">
              <span>{t('subtotal')}</span>
              <FormattedPrice
                pricing={pricing}
                priceValue={priceNet}
                data-testid="subtotal-price"
              />
            </div>
            {Boolean(discount) && (
              <div className="summary-total-line">
                <span>
                  {t('discount')} ({priceBreakdown?.discountRatePoints}%)
                </span>
                <FormattedPrice pricing={pricing} priceValue={discount} data-testid="discount" />
              </div>
            )}
            <div className="summary-total-line">
              <span data-testid="tax-percent">
                {t('tax')} {showTaxValue && <>({(priceBreakdown?.taxRatePoints || 0) / 100}%)</>}
              </span>
              {showTaxValue ? (
                <FormattedPrice pricing={pricing} priceValue={tax} data-testid="tax-price" />
              ) : (
                <span>–</span>
              )}
            </div>
            <div
              className={`summary-total-line  last-total-line ${isMobile ? '' : ' total-line '}`}
            >
              <span>{t('total')}</span>
              <FormattedPrice pricing={pricing} priceValue={priceTotal} data-testid="total-price" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

SummaryLayout.displayName = 'SummaryLayout';
